import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, BarChart3, Target, Sparkles, X, Check } from 'lucide-react';
import { useTracking } from '@/contexts/TrackingContext';

// US-only audience: per legal guidance, we don't need an opt-in pop-up for
// consumers in the US. The banner only auto-prompts on the SEARCH DIRECTORY
// page — never on individual listing pages (/business/*, /live/*, /creators
// /*, /onlybest, etc.) or the homepage. Users can change their choices any
// time via the "Your Privacy Choices" / "Cookie Settings" footer link.
const AUTO_SHOW_PATHS = [
  /^\/directory(\/.*)?$/,
];

/**
 * LocalBest.ai Cookie Consent Banner.
 *
 * - Thin, low-profile bar at the bottom of the screen
 * - Three equal-weight CTAs: Accept All / Reject Non-Essential / Customize
 * - "Customize" opens a dedicated preferences modal with Analytics,
 *   Personalization, and Marketing/Retargeting toggles
 * - Essential cookies always on
 * - Consent stored with timestamp; expires after 12 months (handled in
 *   TrackingContext)
 */
export default function CookieConsentBanner() {
  const {
    consent,
    isConsentBannerVisible,
    consentForceVisible,
    acceptAll,
    rejectAll,
    setCustomConsent,
    closeConsentBanner,
  } = useTracking();

  const location = useLocation();
  const isAutoShowPath = AUTO_SHOW_PATHS.some((re) => re.test(location.pathname));
  // Never render the banner on individual listing pages, the event/live
  // pages, the OnlyBest pages, the creator directory, or any dashboard
  // surface — even if `consentForceVisible` was set earlier on /directory.
  const BLOCK_PATHS = [
    /^\/business\//,
    /^\/live\//,
    /^\/creators(\/.*)?$/,
    /^\/onlybest(\/.*)?$/,
    /^\/leaderboard$/,
    /^\/dashboard(\/.*)?$/,
    /^\/admin(\/.*)?$/,
    /^\/auth\//,
  ];
  const isBlockedPath = BLOCK_PATHS.some((re) => re.test(location.pathname));
  // Render the banner when (a) the user explicitly clicked "Your Privacy
  // Choices" (consentForceVisible), or (b) we're on an auto-show path
  // (currently the /directory page only). Listing pages and dashboard
  // pages never show the banner.
  const shouldRender = !isBlockedPath && isConsentBannerVisible && (consentForceVisible || isAutoShowPath);

  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState({
    analytics: consent.analytics,
    personalization: consent.personalization,
    marketing: consent.marketing,
  });

  // Keep modal draft in sync when banner opens with new consent state
  useEffect(() => {
    if (isConsentBannerVisible) {
      setDraft({
        analytics: consent.analytics,
        personalization: consent.personalization,
        marketing: consent.marketing,
      });
    }
  }, [isConsentBannerVisible]);

  if (!shouldRender) return null;

  const handleSave = () => {
    setCustomConsent(draft);
    setShowModal(false);
  };

  return (
    <>
      {/* === Thin Bottom Banner === */}
      <AnimatePresence>
        {!showModal && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 left-0 right-0 z-[9999] p-3"
            data-testid="cookie-consent-banner"
            role="region"
            aria-label="Cookie consent"
          >
            <div className="max-w-6xl mx-auto">
              <div
                className="backdrop-blur-2xl border border-white/15 shadow-xl rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.85) 100%)',
                }}
              >
                <div className="px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4 flex-wrap md:flex-nowrap">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-4 h-4 text-white" />
                  </div>

                  <p className="text-[#E2E8F0] text-xs sm:text-sm flex-1 min-w-[200px]">
                    We use cookies to improve LocalBest.ai. Essential cookies keep the site working. Optional cookies help us understand visits, improve listings, personalize offers, and support advertising.{" "}
                    <span className="hidden md:inline text-[#94A3B8]">You can accept all, reject non-essential, or customize.</span>
                  </p>

                  <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto justify-end" role="group" aria-label="Cookie choices">
                    <button
                      onClick={acceptAll}
                      className="px-4 h-9 rounded-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:opacity-90 text-white text-xs sm:text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981]/50"
                      data-testid="cookie-accept-all"
                      aria-label="Accept all cookies"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={rejectAll}
                      className="px-4 h-9 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs sm:text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                      data-testid="cookie-reject-all"
                      aria-label="Reject all non-essential cookies"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      onClick={() => setShowModal(true)}
                      className="px-4 h-9 rounded-full bg-transparent hover:bg-white/10 text-[#E2E8F0] text-xs sm:text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                      data-testid="cookie-customize"
                      aria-label="Customize cookie preferences"
                    >
                      Customize
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* === Preferences Modal === */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            data-testid="cookie-customize-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-modal-title"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 16 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-[#0B1226] border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0B1226]/95 backdrop-blur z-10">
                <div>
                  <h3 id="cookie-modal-title" className="text-white font-bold text-lg">Cookie Preferences</h3>
                  <p className="text-xs text-[#94A3B8] mt-0.5">Choose which cookies you allow on LocalBest.ai.</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                  aria-label="Close cookie preferences"
                  data-testid="cookie-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Categories */}
              <div className="p-5 space-y-3">
                <CategoryRow
                  icon={<Shield className="w-4 h-4 text-[#10B981]" />}
                  iconBg="bg-[#10B981]/15"
                  title="Essential"
                  description="Required for the site to function — login, security, and saving your cookie choices."
                  state="always-on"
                />
                <CategoryRow
                  icon={<BarChart3 className="w-4 h-4 text-[#00E5FF]" />}
                  iconBg="bg-[#00E5FF]/15"
                  title="Analytics"
                  description="Help us understand which listings and pages are most useful so we can improve LocalBest.ai."
                  state={draft.analytics ? "on" : "off"}
                  onToggle={() => setDraft((p) => ({ ...p, analytics: !p.analytics }))}
                  testid="cookie-toggle-analytics"
                />
                <CategoryRow
                  icon={<Sparkles className="w-4 h-4 text-[#7C3AED]" />}
                  iconBg="bg-[#7C3AED]/15"
                  title="Personalization"
                  description="Remember your location and preferences so we can highlight the most relevant Best of Best listings and offers."
                  state={draft.personalization ? "on" : "off"}
                  onToggle={() => setDraft((p) => ({ ...p, personalization: !p.personalization }))}
                  testid="cookie-toggle-personalization"
                />
                <CategoryRow
                  icon={<Target className="w-4 h-4 text-[#F59E0B]" />}
                  iconBg="bg-[#F59E0B]/15"
                  title="Marketing / Retargeting"
                  description="Allow LocalBest.ai and partners to measure ad campaigns and show you relevant promotions across the web."
                  state={draft.marketing ? "on" : "off"}
                  onToggle={() => setDraft((p) => ({ ...p, marketing: !p.marketing }))}
                  testid="cookie-toggle-marketing"
                />
              </div>

              {/* Footer actions */}
              <div className="p-5 border-t border-white/10 sticky bottom-0 bg-[#0B1226]/95 backdrop-blur space-y-2">
                <button
                  onClick={handleSave}
                  className="w-full h-10 rounded-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:opacity-90 text-white font-semibold text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981]/50"
                  data-testid="cookie-save-preferences"
                >
                  Save My Preferences
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { acceptAll(); setShowModal(false); }}
                    className="h-9 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs font-medium transition"
                    data-testid="cookie-modal-accept-all"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={() => { rejectAll(); setShowModal(false); }}
                    className="h-9 rounded-full bg-transparent hover:bg-white/10 border border-white/10 text-[#E2E8F0] text-xs font-medium transition"
                    data-testid="cookie-modal-reject-all"
                  >
                    Reject Non-Essential
                  </button>
                </div>
                <p className="text-[10px] text-center text-[#64748B] pt-1">
                  You can change these choices anytime from the "Cookie Settings" link in the footer.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function CategoryRow({ icon, iconBg, title, description, state, onToggle, testid }) {
  const isOn = state === "on" || state === "always-on";
  const alwaysOn = state === "always-on";
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h4 className="text-white font-semibold text-sm">{title}</h4>
          {alwaysOn ? (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] flex items-center gap-1 font-semibold">
              <Check className="w-3 h-3" /> Always On
            </span>
          ) : (
            <button
              type="button"
              onClick={onToggle}
              role="switch"
              aria-checked={isOn}
              aria-label={`Toggle ${title} cookies`}
              data-testid={testid}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${isOn ? "bg-gradient-to-r from-[#10B981] to-[#059669]" : "bg-white/15"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isOn ? "left-[22px]" : "left-0.5"}`} />
            </button>
          )}
        </div>
        <p className="text-xs text-[#94A3B8] leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
